import type { PlayerLineup } from '@repo/types';
import { GoalType } from '@repo/types';
import {
  ballIcon,
  ownGoalIcon,
  penaltyGoalIcon,
  secondYellowCardIcon,
  subsArrowsIcon,
} from '../../../common/staticAssets.constants';
import { formatMinuteDisplay, formatPhaseAwareMinute } from '../../../utils/formatMatchMinute';
import type { TimelineEventType } from './buildTimeline';

type LineupGoal = NonNullable<PlayerLineup['goals']>[number];

export type LineupIconEvent = {
  key: string;
  variant: 'icon';
  minuteLabel: string;
  iconSrc: string;
  iconAlt: string;
};

export type LineupCardEvent = {
  key: string;
  variant: 'card';
  minuteLabel: string;
  cardModifier: 'yellow' | 'red';
  label: string;
};

export type LineupEvent = LineupIconEvent | LineupCardEvent;

/**
 * Branches on the stored `Goal.goalType`, which `mapLineup` now carries through (SC-718).
 *
 * This used to sniff penalty/own-goal out of a free-text `description`, and that could never
 * fire: the stored `Goal` schema has no `description` field at all and `mapLineup` emitted none,
 * so every penalty rendered a plain ball. Reading the real field is both correct and testable.
 *
 * A goal imported before `goalType` existed has none, and falls through to the plain ball —
 * which is what it rendered before, so no historical match regresses.
 */
export const resolveGoalGlyph = (goal: LineupGoal): { src: string; alt: string } => {
  if (goal.goalType === GoalType.OwnGoal) {
    return { src: ownGoalIcon, alt: 'Own goal' };
  }
  if (goal.goalType === GoalType.Penalty) {
    return { src: penaltyGoalIcon, alt: 'Penalty goal' };
  }
  return { src: ballIcon, alt: 'Goal' };
};

const buildGoalEvents = (lineup: PlayerLineup, hasExtraTime: boolean): LineupIconEvent[] =>
  (lineup.goals ?? []).map((goal, index) => {
    const glyph = resolveGoalGlyph(goal);
    return {
      key: `goal-${index}`,
      variant: 'icon',
      minuteLabel: `${formatPhaseAwareMinute(goal.minute, goal.phase, hasExtraTime)}'`,
      iconSrc: glyph.src,
      iconAlt: glyph.alt,
    };
  });

const buildSubEvents = (lineup: PlayerLineup, hasExtraTime: boolean): LineupIconEvent[] => {
  const events: LineupIconEvent[] = [];
  if (lineup.replacedBy) {
    events.push({
      key: 'sub-off',
      variant: 'icon',
      minuteLabel: `${formatMinuteDisplay(lineup.replacedBy.minute, hasExtraTime)}'`,
      iconSrc: subsArrowsIcon,
      iconAlt: 'Substituted off',
    });
  }
  if (lineup.replaces) {
    events.push({
      key: 'sub-on',
      variant: 'icon',
      minuteLabel: `${formatMinuteDisplay(lineup.replaces.minute, hasExtraTime)}'`,
      iconSrc: subsArrowsIcon,
      iconAlt: 'Substituted on',
    });
  }
  return events;
};

// yellowCards[0] is always the (only, or first) yellow — a plain CSS card block.
// yellowCards[1], when present, is the second yellow that sent the player off —
// shown with the dedicated secondYellowCardIcon image instead of another plain card.
const buildYellowCardEvents = (lineup: PlayerLineup, hasExtraTime: boolean): LineupEvent[] =>
  (lineup.yellowCards ?? []).map((minute, index) => {
    const minuteLabel = `${formatMinuteDisplay(minute, hasExtraTime)}'`;
    if (index === 1) {
      return {
        key: 'yellow-2',
        variant: 'icon',
        minuteLabel,
        iconSrc: secondYellowCardIcon,
        iconAlt: 'Second yellow card',
      };
    }
    return { key: 'yellow-1', variant: 'card', minuteLabel, cardModifier: 'yellow', label: 'Yellow card' };
  });

const buildRedCardEvent = (lineup: PlayerLineup, hasExtraTime: boolean): LineupCardEvent[] =>
  lineup.redCard
    ? [
        {
          key: 'red',
          variant: 'card',
          minuteLabel: `${formatMinuteDisplay(lineup.redCard, hasExtraTime)}'`,
          cardModifier: 'red',
          label: 'Red card',
        },
      ]
    : [];

/**
 * Derives the ordered list of event glyphs for a single lineup row (goals, then
 * substitution off/on, then cards) — mirrors the field order the old MatchOverview
 * rendered per player (apps/ui .../MatchOverview/components/TeamTab.tsx).
 */
export const buildLineupEvents = (lineup: PlayerLineup, hasExtraTime: boolean): LineupEvent[] => [
  ...buildGoalEvents(lineup, hasExtraTime),
  ...buildSubEvents(lineup, hasExtraTime),
  ...buildYellowCardEvents(lineup, hasExtraTime),
  ...buildRedCardEvent(lineup, hasExtraTime),
];

// ── Timeline tab (Task 6) glyphs — same icon/card variant shapes as
// LineupEvent above, keyed directly by buildTimeline's TimelineEventType
// rather than sniffed from free-text. `second-yellow` reuses the same
// dedicated icon as a lineup row's second booking (not a plain card), and
// plain yellow/red reuse the flat CSS-card convention (see match-timeline__card
// in match-detail-panels.scss, mirroring match-lineups__card).
export type TimelineIconGlyph = { variant: 'icon'; iconSrc: string; iconAlt: string };
export type TimelineCardGlyph = { variant: 'card'; cardModifier: 'yellow' | 'red'; label: string };
export type TimelineGlyph = TimelineIconGlyph | TimelineCardGlyph;

const TIMELINE_ICONS: Record<'goal' | 'penalty' | 'own-goal' | 'sub', { src: string; alt: string }> = {
  goal: { src: ballIcon, alt: 'Goal' },
  penalty: { src: penaltyGoalIcon, alt: 'Penalty' },
  'own-goal': { src: ownGoalIcon, alt: 'Own goal' },
  sub: { src: subsArrowsIcon, alt: 'Substitution' },
};

export const resolveTimelineGlyph = (type: TimelineEventType): TimelineGlyph => {
  if (type === 'yellow') {
    return { variant: 'card', cardModifier: 'yellow', label: 'Yellow card' };
  }
  if (type === 'red') {
    return { variant: 'card', cardModifier: 'red', label: 'Red card' };
  }
  if (type === 'second-yellow') {
    return { variant: 'icon', iconSrc: secondYellowCardIcon, iconAlt: 'Second yellow card' };
  }
  const icon = TIMELINE_ICONS[type];
  return { variant: 'icon', iconSrc: icon.src, iconAlt: icon.alt };
};
